/**
 * Turns a `FileRef` into the `PreviewFile` FilePreviewContent renders
 * (cdb_webos#1619): loads the bytes, owns the object URL and revokes it when
 * the reference changes or the preview goes away.
 */
import { useEffect, useState } from 'react'
import { logError } from '@/lib/logger'
import type { FileRef } from '@/lib/files/fileRef'
import { classifyFile } from './fileType'
import type { PreviewFile } from './types'

export type FileRefPreviewState =
  | { status: 'loading' }
  | { status: 'ready'; file: PreviewFile }
  | { status: 'error' }

function previewFile(ref: FileRef, fileUrl: string): PreviewFile {
  return {
    name: ref.fileName,
    fileName: ref.fileName,
    fileUrl,
    ...(ref.kind === 'knowledge-area' ? { documentId: ref.documentId, knowledgeArea: ref.knowledgeArea } : {}),
  }
}

export function useFileRefPreview(ref: FileRef | undefined): FileRefPreviewState {
  const [state, setState] = useState<FileRefPreviewState>({ status: 'loading' })
  // A new object with the same fields is the same file.
  const key = ref ? JSON.stringify(ref) : ''

  useEffect(() => {
    if (!ref) return
    // A Knowledge Area video streams by range inside the renderer — never
    // fetch the whole file up front.
    if (ref.kind === 'knowledge-area' && classifyFile(ref.fileName) === 'video') {
      setState({ status: 'ready', file: previewFile(ref, '') })
      return
    }

    let active = true
    let url: string | null = null
    setState({ status: 'loading' })
    import('@/lib/files/loadFileRef')
      .then(({ loadFileRef }) => loadFileRef(ref, 'preview'))
      .then((loaded) => {
        if (!active) {
          URL.revokeObjectURL(loaded)
          return
        }
        url = loaded
        setState({ status: 'ready', file: previewFile(ref, loaded) })
      })
      .catch((err) => {
        logError(err as Error, { context: 'useFileRefPreview', kind: ref.kind, fileName: ref.fileName })
        if (active) setState({ status: 'error' })
      })

    return () => {
      active = false
      // Delayed: the renderers still hold the URL while the window animates
      // out, and StrictMode remounts once in development.
      if (url) {
        const stale = url
        setTimeout(() => URL.revokeObjectURL(stale), 2000)
      }
    }
    // `key` stands for `ref`'s fields.
  }, [key])

  return state
}
